import { password, VERCEL_SERVER_URL } from "../../config/constants"
import { BaseCache } from "./base-cache"

const CACHE_NAME = "book-cache"

const handleResult = (responseData: any)=> {
  return parseResponse(responseData.message)
}

class BookCache extends BaseCache {
  constructor() {
    super(CACHE_NAME)
  }

  async deleteAll() {
    if (!this.cache) {
      await this.init()
    }
    if (!this.cache) return
    const keys = await this.cache.keys()
    await Promise.all(keys.map(key=> this.cache?.delete(key)))
  }

  async getBookData(bookIndex: string): Promise<string[]> {
    const url = `${VERCEL_SERVER_URL}/books/${bookIndex}`
    if (!this.cache) {
      await this.init()
    }
    if (!this.cache) {
      throw new Error("Cache API not supported")
    }

    const cachedResponse = await this.cache.match(url)
    if (cachedResponse) {
      const responseData = await cachedResponse.json()
      return handleResult(responseData)
    }

    try {
      const res = await fetch(url, {
        body: JSON.stringify({ password }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      })
      if (res.ok) {
        this.cache.put(url, res.clone())
        const responseData = await res.json()
        return handleResult(responseData)
      }
      throw new Error(`Failed to fetch book: ${url}`)
    } catch (e) {
      throw new Error(`Failed to cache book: ${url}`, {cause: e})
    }
  }

  async getBookIndexes(): Promise<Record<string, string>> {
    const url = `${VERCEL_SERVER_URL}/books`
    if (!this.cache) {
      await this.init()
    }
    if (!this.cache) {
      throw new Error("Cache API not supported")
    }

    const cachedResponse = await this.cache.match(url)
    if (cachedResponse) {
      const responseData = await cachedResponse.json()
      return parseBookIndexesResponse(responseData.message)
    }

    try {
      const res = await fetch(url)
      if (res.ok) {
        this.cache.put(url, res.clone())
        const responseData = await res.json()
        return parseBookIndexesResponse(responseData.message)
      }
      throw new Error(`Failed to fetch book indexes: ${url}`)
    } catch {
      throw new Error(`Failed to cache book indexes: ${url}`)
    }
  }
}

function parseBookIndexesResponse(response: string): Record<string, string> {
  // SAFETY: parsed from JSON, API response has data field
  return JSON.parse(response).data as Record<string, string>
}
function parseResponse(response: string) {
  // SAFETY: parsed from JSON, API response has data field as string array
  return (JSON.parse(response).data as string[]).filter(
    (x: string)=> x.trim().length > 0,
  )
}

export const bookCache = new BookCache()
